import {
  loadAutoFormaFullPullClient,
  loadAutoFormaLiveClient,
  loadAutoFormaProjectSnapshotClient,
  pullFullFormaSnapshot,
  type Building,
  type FormaApiCallStatus,
  type FormaFullPullClient,
  type FormaGeometryExchangeClient,
  type FormaNativeAnalysisClient,
  type FormaProjectSnapshotClient,
  type FormaWindPredictionClient,
  type FullFormaPullResult,
  type MunicipalityId,
  type WindComfortScale,
} from '@enersu/core-orchestrator';
import {
  buildHebrewReportShell,
  createReportPdfBlob,
  HEBREW_REPORT_FONTS,
  type GenericAnalysisOutput,
  type PdfMakeClient,
  type PdfMakeVfs,
} from '@enersu/report-generator';
import { useEffect, useState } from 'preact/hooks';

import type { PilotBackendClient, PilotBackendSaveResult } from './backend-client';
import { PANEL_STRINGS, type PanelLang } from './i18n';
import { runLocalPilotPipeline } from './local-pilot-pipeline';
import { MUNICIPALITIES, nearestMunicipality, type Municipality } from './municipalities';
import type { SupabaseSnapshotClient } from './supabase-client';

type PipelineState = 'idle' | 'running' | 'complete' | 'error';
type PdfState = 'idle' | 'creating' | 'ready' | 'error';
type FullPullState = 'idle' | 'pulling' | 'done' | 'error';
type SupabaseSaveState = 'disabled' | 'idle' | 'saving' | 'saved' | 'error';
type BackendSaveState = 'disabled' | 'idle' | 'saving' | 'saved' | 'error';
type PipelineRunSummary = {
  analyses: number;
  needsReview: number;
} | null;
type AppProps = {
  analysisClient?: FormaNativeAnalysisClient;
  autoRun?: boolean;
  windClient?: FormaWindPredictionClient;
  geometryClient?: FormaGeometryExchangeClient;
  fullPullClient?: FormaFullPullClient;
  supabaseClient?: SupabaseSnapshotClient;
  backendClient?: PilotBackendClient;
  defaultBuildingUseType?: Building['useType'];
  forma?: FormaProjectSnapshotClient;
  defaultMunicipalityId?: MunicipalityId;
  defaultProjectName?: string;
  fontVfs?: PdfMakeVfs;
  onReportReady?: (report: ReturnType<typeof buildHebrewReportShell>) => void;
  onPdfBlobReady?: (blob: Blob) => void;
  pdfMake?: PdfMakeClient;
};

interface LiveScreeningClients {
  native?: FormaNativeAnalysisClient | undefined;
  wind?: FormaWindPredictionClient | undefined;
  geometry?: FormaGeometryExchangeClient | undefined;
}

/**
 * Default date for live native sun screening: the winter solstice, a
 * conservative worst-case (shortest day / least sun) for sun-hours screening in
 * the northern hemisphere. Surfaced as a constant rather than a UI control to
 * keep the pilot panel minimal.
 */
const DEFAULT_NATIVE_SUN_DATE = { month: 12, date: 21 } as const;

export function App({
  analysisClient,
  autoRun = false,
  windClient,
  geometryClient,
  fullPullClient,
  supabaseClient,
  backendClient,
  defaultBuildingUseType = 'unknown',
  defaultMunicipalityId = 'tel-aviv',
  defaultProjectName = 'EnerSU pilot',
  fontVfs,
  forma,
  onPdfBlobReady,
  onReportReady,
  pdfMake,
}: AppProps) {
  const [buildingUseType, setBuildingUseType] =
    useState<Building['useType']>(defaultBuildingUseType);
  const [municipalityId, setMunicipalityId] = useState<MunicipalityId>(defaultMunicipalityId);
  const [detectedMunicipality, setDetectedMunicipality] = useState<{
    municipality: Municipality;
    lat: number;
    lon: number;
  } | null>(null);
  const [projectMeta, setProjectMeta] = useState<{
    countryCode: string;
    timezone: string;
  } | null>(null);
  const [lang, setLang] = useState<PanelLang>('en');
  const t = PANEL_STRINGS[lang];
  const [projectName, setProjectName] = useState(defaultProjectName);
  const [runSun, setRunSun] = useState(false);
  const [runNoise, setRunNoise] = useState(false);
  const [runWind, setRunWind] = useState(false);
  const [sunMonth, setSunMonth] = useState<number>(DEFAULT_NATIVE_SUN_DATE.month);
  const [sunDate, setSunDate] = useState<number>(DEFAULT_NATIVE_SUN_DATE.date);
  const [windComfortScale, setWindComfortScale] = useState<WindComfortScale>('lawson_lddc');
  const [pipelineState, setPipelineState] = useState<PipelineState>('idle');
  const [progress, setProgress] = useState(0);
  const [reportDefinition, setReportDefinition] = useState<ReturnType<
    typeof buildHebrewReportShell
  > | null>(null);
  const [runSummary, setRunSummary] = useState<PipelineRunSummary>(null);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [liveScreeningNotice, setLiveScreeningNotice] = useState<string | null>(null);
  const [analysisOutputs, setAnalysisOutputs] = useState<GenericAnalysisOutput[]>([]);
  const [pdfState, setPdfState] = useState<PdfState>('idle');
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState<string | null>(null);
  const [pdfErrorMessage, setPdfErrorMessage] = useState<string | null>(null);
  const [backendSaveState, setBackendSaveState] = useState<BackendSaveState>(
    backendClient === undefined ? 'disabled' : 'idle',
  );
  const [backendSaveResult, setBackendSaveResult] = useState<PilotBackendSaveResult | null>(null);
  const [backendErrorMessage, setBackendErrorMessage] = useState<string | null>(null);
  const [fullPullState, setFullPullState] = useState<FullPullState>('idle');
  const [fullPullResult, setFullPullResult] = useState<FullFormaPullResult | null>(null);
  const [fullPullError, setFullPullError] = useState<string | null>(null);
  const [fullPullDownloadUrl, setFullPullDownloadUrl] = useState<string | null>(null);
  const [supabaseSaveState, setSupabaseSaveState] = useState<SupabaseSaveState>(
    supabaseClient === undefined ? 'disabled' : 'idle',
  );
  const [supabaseError, setSupabaseError] = useState<string | null>(null);
  const isRunning = pipelineState === 'running';
  const canGeneratePdf =
    reportDefinition !== null && fontVfs !== undefined && pdfMake !== undefined;

  useEffect(() => {
    return () => revokeBlobDownloadUrl(pdfDownloadUrl);
  }, [pdfDownloadUrl]);

  useEffect(() => {
    return () => revokeBlobDownloadUrl(fullPullDownloadUrl);
  }, [fullPullDownloadUrl]);

  // Pre-select the municipality from the Forma project's location and surface
  // basic project context. No-op outside Forma; a project far from any known
  // municipality leaves the manual selection in place.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const client = await resolveProjectClient(forma);
      if (client === undefined) {
        return;
      }

      const [geo, metadata] = await Promise.all([
        client.project.getGeoLocation().catch(() => undefined),
        client.project.get().catch(() => undefined),
      ]);

      if (cancelled) {
        return;
      }

      if (metadata !== undefined) {
        setProjectMeta({ countryCode: metadata.countryCode, timezone: metadata.timezone });
      }

      if (geo === undefined) {
        return;
      }

      const match = nearestMunicipality(geo[0], geo[1]);
      if (match === undefined) {
        return;
      }

      setMunicipalityId(match.id);
      setDetectedMunicipality({ municipality: match, lat: geo[0], lon: geo[1] });
    })();

    return () => {
      cancelled = true;
    };
  }, [forma]);

  const runFullPull = async () => {
    setFullPullState('pulling');
    setFullPullResult(null);
    setFullPullError(null);
    setFullPullDownloadUrl(null);
    setSupabaseSaveState(supabaseClient === undefined ? 'disabled' : 'idle');
    setSupabaseError(null);

    const client = await resolveFullPullClient(fullPullClient);
    if (client === undefined) {
      setFullPullError('No Forma SDK connection. Open this panel inside Forma to pull live data.');
      setFullPullState('error');
      return;
    }

    try {
      const result = await pullFullFormaSnapshot({
        forma: client,
        pulledAt: new Date().toISOString(),
      });
      setFullPullResult(result);
      setFullPullDownloadUrl(createJsonDownloadUrl(result.snapshot));
      setFullPullState('done');
    } catch (error) {
      setFullPullError(normalizePdfError(error));
      setFullPullState('error');
    }
  };

  const saveSnapshotToSupabase = async () => {
    if (supabaseClient === undefined || fullPullResult === null) {
      return;
    }

    setSupabaseSaveState('saving');
    setSupabaseError(null);

    try {
      await supabaseClient.saveSnapshot(fullPullResult);
      setSupabaseSaveState('saved');
    } catch (error) {
      setSupabaseError(normalizePdfError(error));
      setSupabaseSaveState('error');
    }
  };

  const runMockPipeline = async () => {
    setPipelineState('running');
    setProgress(15);
    setPdfState('idle');
    setPdfDownloadUrl(null);
    setPdfErrorMessage(null);
    setAnalysisOutputs([]);
    setReportDefinition(null);
    setRunSummary(null);
    setPipelineError(null);
    setLiveScreeningNotice(null);
    setBackendSaveResult(null);
    setBackendErrorMessage(null);
    setBackendSaveState(backendClient === undefined ? 'disabled' : 'idle');

    try {
      await Promise.resolve();
      setProgress(60);

      const anyLive = runSun || runNoise || runWind;
      const live = await resolveLiveScreening(anyLive, {
        native: analysisClient,
        wind: windClient,
        geometry: geometryClient,
      });
      // Analyses were selected but no Forma connection resolved: tell the user the
      // run used staged inputs instead of silently dropping the request.
      if (
        anyLive &&
        live.native === undefined &&
        live.wind === undefined &&
        live.geometry === undefined
      ) {
        setLiveScreeningNotice(
          'Live screening was requested but no Forma connection was available — ran with staged inputs only.',
        );
      }
      // Only resolve a native client when sun or noise is selected, so each
      // analysis runs independently of the others.
      const nativeClient = runSun || runNoise ? live.native : undefined;
      const result = await runLocalPilotPipeline({
        buildingUseType,
        forma,
        municipalityId,
        projectName,
        ...(anyLive && live.geometry !== undefined ? { geometryClient: live.geometry } : {}),
        ...(nativeClient !== undefined
          ? {
              nativeScreening: {
                client: nativeClient,
                ...(runSun ? { sun: { month: sunMonth, date: sunDate } } : {}),
                ...(runNoise ? { noise: {} } : {}),
              },
            }
          : {}),
        ...(runWind && live.wind !== undefined
          ? { windScreening: { prediction: live.wind, comfortScale: windComfortScale } }
          : {}),
      });

      setProgress(85);
      setAnalysisOutputs(result.analyses);
      setReportDefinition(result.report);
      setRunSummary({ analyses: result.analyses.length, needsReview: result.needsReview });
      onReportReady?.(result.report);

      if (backendClient !== undefined) {
        setBackendSaveState('saving');

        try {
          const saved = await backendClient.savePipelineRun({
            analyses: result.analyses,
            failed: result.failed,
            needsReview: result.needsReview,
            project: result.project,
          });

          setBackendSaveResult(saved);
          setBackendSaveState('saved');
        } catch (error) {
          setBackendErrorMessage(normalizePdfError(error));
          setBackendSaveState('error');
        }
      }

      setProgress(100);
      setPipelineState('complete');
    } catch (error) {
      setPipelineError(normalizePdfError(error));
      setProgress(100);
      setPipelineState('error');
    }
  };

  // Run the staging pipeline as soon as the panel opens when requested, so the
  // user sees results without clicking. Off by default (tests/standalone);
  // enabled from main.tsx when embedded in Forma.
  useEffect(() => {
    if (autoRun) {
      void runMockPipeline();
    }
  }, [autoRun]);

  const generatePdf = async () => {
    if (!canGeneratePdf) {
      return;
    }

    setPdfState('creating');
    setPdfDownloadUrl(null);
    setPdfErrorMessage(null);

    try {
      const blob = await createReportPdfBlob({
        documentDefinition: reportDefinition,
        fonts: HEBREW_REPORT_FONTS,
        pdfMake,
        vfs: fontVfs,
      });
      onPdfBlobReady?.(blob);
      setPdfDownloadUrl(createBlobDownloadUrl(blob));
      setPdfState('ready');
    } catch (error) {
      setPdfErrorMessage(normalizePdfError(error));
      setPdfState('error');
    }
  };

  return (
    <div class="enersu-panel" dir={t.dir}>
      <header>
        <div class="lang-toggle" role="group" aria-label="Language">
          <button type="button" class={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>
            EN
          </button>
          <button type="button" class={lang === 'he' ? 'active' : ''} onClick={() => setLang('he')}>
            עברית
          </button>
        </div>
        <h1>EnerSU Compliance</h1>
        <p class="subtitle">{t.subtitle}</p>
        <p class="screening-disclaimer" role="note">
          {t.disclaimer}
        </p>
      </header>
      <main>
        <section class="pipeline-panel" aria-labelledby="pipeline-title">
          <div>
            <h2 id="pipeline-title">{t.studyPackage}</h2>
            <p class="panel-copy">{t.studyPackageCopy}</p>
          </div>

          <div class="input-grid">
            <label>
              <span>{t.projectName}</span>
              <input
                type="text"
                value={projectName}
                onInput={(event) => setProjectName(event.currentTarget.value)}
              />
            </label>
            <label>
              <span>{t.municipality}</span>
              <select
                value={municipalityId}
                onChange={(event) => {
                  setMunicipalityId(event.currentTarget.value as MunicipalityId);
                  setDetectedMunicipality(null);
                }}
              >
                {MUNICIPALITIES.map((municipality) => (
                  <option value={municipality.id} key={municipality.id}>
                    {municipality.label}
                    {municipality.pilot ? ' · pilot' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t.buildingType}</span>
              <select
                value={buildingUseType}
                onChange={(event) =>
                  setBuildingUseType(event.currentTarget.value as Building['useType'])
                }
              >
                <option value="unknown">{t.buildingTypes.unknown}</option>
                <option value="residential">{t.buildingTypes.residential}</option>
                <option value="office">{t.buildingTypes.office}</option>
                <option value="mixed-use">{t.buildingTypes['mixed-use']}</option>
                <option value="public">{t.buildingTypes.public}</option>
              </select>
            </label>
          </div>

          {detectedMunicipality !== null ? (
            <p class="detected-note">
              {t.detectedPrefix} {detectedMunicipality.municipality.label} (
              {detectedMunicipality.lat.toFixed(3)}, {detectedMunicipality.lon.toFixed(3)})
              {projectMeta !== null ? ` · ${projectMeta.timezone}` : ''}
            </p>
          ) : null}
          <p class="municipality-hint">{t.municipalityHint}</p>
          {projectMeta !== null && projectMeta.countryCode !== 'IL' ? (
            <p class="warning-note">{t.outsideIsrael(projectMeta.countryCode)}</p>
          ) : null}

          <fieldset class="analysis-picker" aria-label="Live screening analyses">
            <legend>{t.pickAnalyses}</legend>
            <p class="panel-copy">{t.pickAnalysesCopy}</p>
            <label class="check-row">
              <input
                type="checkbox"
                checked={runSun}
                onChange={(event) => setRunSun(event.currentTarget.checked)}
              />
              <span>{t.sun}</span>
            </label>
            <label class="check-row">
              <input
                type="checkbox"
                checked={runNoise}
                onChange={(event) => setRunNoise(event.currentTarget.checked)}
              />
              <span>{t.noise}</span>
            </label>
            <label class="check-row">
              <input
                type="checkbox"
                checked={runWind}
                onChange={(event) => setRunWind(event.currentTarget.checked)}
              />
              <span>{t.wind}</span>
            </label>
          </fieldset>

          {runSun || runWind ? (
            <div class="input-grid" aria-label="Live screening options">
              {runSun ? (
                <>
                  <label>
                    <span>{t.sunMonth}</span>
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={sunMonth}
                      onInput={(event) =>
                        setSunMonth(
                          parseBoundedInt(
                            event.currentTarget.value,
                            1,
                            12,
                            DEFAULT_NATIVE_SUN_DATE.month,
                          ),
                        )
                      }
                    />
                  </label>
                  <label>
                    <span>{t.sunDay}</span>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={sunDate}
                      onInput={(event) =>
                        setSunDate(
                          parseBoundedInt(
                            event.currentTarget.value,
                            1,
                            31,
                            DEFAULT_NATIVE_SUN_DATE.date,
                          ),
                        )
                      }
                    />
                  </label>
                </>
              ) : null}
              {runWind ? (
                <label>
                  <span>{t.windScale}</span>
                  <select
                    value={windComfortScale}
                    onChange={(event) =>
                      setWindComfortScale(event.currentTarget.value as WindComfortScale)
                    }
                  >
                    <option value="lawson_lddc">Lawson LDDC</option>
                    <option value="davenport">Davenport</option>
                    <option value="lawson">Lawson</option>
                    <option value="lawson_2001">Lawson 2001</option>
                    <option value="nen8100">NEN 8100</option>
                    <option value="cstb">CSTB</option>
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}

          <div class="status-card" role="status" aria-live="polite">
            <span class="status-label">{t.statusLabel}</span>
            <strong>{t.statusText[pipelineState]}</strong>
            <div class="progress-row">
              <div
                class="progress-track"
                role="progressbar"
                aria-label={t.progressLabel}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <span class="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span class="progress-value">{progress}%</span>
            </div>
          </div>

          {backendSaveState !== 'disabled' ? (
            <div class="backend-card" aria-label="Backend persistence">
              <span class="status-label">Backend</span>
              <strong>{formatBackendSaveStatus(backendSaveState)}</strong>
              {backendSaveResult !== null ? (
                <dl>
                  <div>
                    <dt>Project</dt>
                    <dd>{backendSaveResult.projectId}</dd>
                  </div>
                  <div>
                    <dt>Run</dt>
                    <dd>{backendSaveResult.pipelineRunId}</dd>
                  </div>
                  <div>
                    <dt>Report</dt>
                    <dd>{backendSaveResult.reportId}</dd>
                  </div>
                </dl>
              ) : null}
              {backendSaveState === 'error' && backendErrorMessage !== null ? (
                <p>{backendErrorMessage}</p>
              ) : null}
            </div>
          ) : null}

          <button type="button" onClick={runMockPipeline} disabled={isRunning}>
            {t.runPipeline}
          </button>

          {reportDefinition !== null ? (
            <button
              type="button"
              onClick={generatePdf}
              disabled={!canGeneratePdf || pdfState === 'creating'}
            >
              {t.generatePdf}
            </button>
          ) : null}

          {pdfState === 'ready' && pdfDownloadUrl !== null ? (
            <a class="download-link" href={pdfDownloadUrl} download="enersu-compliance-pilot.pdf">
              {t.downloadPdf}
            </a>
          ) : null}

          {analysisOutputs.length > 0 ? (
            <div class="analysis-list" aria-label="Prepared analyses">
              {analysisOutputs.map((analysis) => (
                <article class="analysis-item" key={analysis.id}>
                  <div class="analysis-heading">
                    <strong>{analysis.title}</strong>
                    <span
                      class={`analysis-status status-${analysisStatusVariant(analysis.status)}`}
                    >
                      {t.analysisStatus[analysis.status]}
                    </span>
                  </div>
                  {analysis.summary !== undefined ? <p>{analysis.summary}</p> : null}
                  {analysis.metrics !== undefined && analysis.metrics.length > 0 ? (
                    <dl>
                      {analysis.metrics.map((metric) => (
                        <div key={metric.label}>
                          <dt>{metric.label}</dt>
                          <dd>{formatAnalysisMetric(metric)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </article>
              ))}
            </div>
          ) : null}

          {liveScreeningNotice !== null ? <p class="run-note">{liveScreeningNotice}</p> : null}

          <p class="run-note">
            {formatRunNote({
              isRunning,
              canGeneratePdf,
              hasReport: reportDefinition !== null,
              pdfErrorMessage,
              pipelineError,
              pdfState,
              pipelineState,
              runSummary,
            })}
          </p>
        </section>

        <details class="advanced">
          <summary>Advanced · API verification &amp; full data pull</summary>
          <p class="panel-copy">
            Pull every available Forma surface and verify which API calls respond against the live
            model.
          </p>

          <button type="button" onClick={runFullPull} disabled={fullPullState === 'pulling'}>
            {fullPullState === 'pulling'
              ? 'Pulling Forma data…'
              : 'Pull all Forma data & verify API'}
          </button>

          {fullPullState === 'error' && fullPullError !== null ? (
            <p class="run-note">{fullPullError}</p>
          ) : null}

          {fullPullResult !== null ? (
            <div class="pull-report" aria-label="API verification report">
              <p class="pull-summary">
                {fullPullResult.okCount} OK · {fullPullResult.errorCount} error ·{' '}
                {fullPullResult.unavailableCount} unavailable
              </p>
              <ul class="api-report">
                {fullPullResult.apiReport.map((entry) => (
                  <li key={entry.surface} class={`api-row api-${entry.status}`}>
                    <span class="api-icon" aria-hidden="true">
                      {apiStatusIcon(entry.status)}
                    </span>
                    <span class="api-surface">{entry.surface}</span>
                    {entry.count !== undefined ? (
                      <span class="api-count">{entry.count}</span>
                    ) : null}
                    {entry.error !== undefined ? (
                      <span class="api-error">{entry.error}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
              {fullPullDownloadUrl !== null ? (
                <a
                  class="download-link"
                  href={fullPullDownloadUrl}
                  download="forma-full-snapshot.json"
                >
                  Download snapshot JSON
                </a>
              ) : null}

              {supabaseSaveState === 'disabled' ? (
                <p class="run-note">Supabase persistence is not configured for this build.</p>
              ) : (
                <div class="supabase-row" aria-label="Supabase persistence">
                  <button
                    type="button"
                    onClick={saveSnapshotToSupabase}
                    disabled={supabaseSaveState === 'saving'}
                  >
                    {supabaseSaveState === 'saving' ? 'Saving to Supabase…' : 'Save to Supabase'}
                  </button>
                  <span class="supabase-status">{formatSupabaseSaveStatus(supabaseSaveState)}</span>
                  {supabaseSaveState === 'error' && supabaseError !== null ? (
                    <span class="supabase-error">{supabaseError}</span>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </details>
      </main>
    </div>
  );
}

function createBlobDownloadUrl(blob: Blob): string | null {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null;
  }

  return URL.createObjectURL(blob);
}

function revokeBlobDownloadUrl(downloadUrl: string | null): void {
  if (
    downloadUrl !== null &&
    typeof URL !== 'undefined' &&
    typeof URL.revokeObjectURL === 'function'
  ) {
    URL.revokeObjectURL(downloadUrl);
  }
}

/**
 * Resolves the live-screening clients for a pipeline run. Returns an empty set
 * when the toggle is off. Injected clients win (keeping the path testable);
 * otherwise the single auto Forma SDK backs the analysis, prediction, and
 * geometry surfaces at once. A failed auto lookup (e.g. outside Forma) degrades
 * to whatever was injected instead of throwing.
 */
async function resolveLiveScreening(
  enabled: boolean,
  injected: LiveScreeningClients,
): Promise<LiveScreeningClients> {
  if (!enabled) {
    return {};
  }

  let native = injected.native;
  let wind = injected.wind;
  let geometry = injected.geometry;

  if (native === undefined || wind === undefined || geometry === undefined) {
    try {
      const auto = await loadAutoFormaLiveClient();
      native = native ?? auto;
      wind = wind ?? auto;
      geometry = geometry ?? auto;
    } catch {
      // Auto SDK unavailable (e.g. outside Forma); keep whatever was injected.
    }
  }

  return {
    ...(native === undefined ? {} : { native }),
    ...(wind === undefined ? {} : { wind }),
    ...(geometry === undefined ? {} : { geometry }),
  };
}

/**
 * Resolves a full-pull client. An injected client wins (keeping the path
 * testable); otherwise the auto Forma SDK is loaded. Returns undefined when no
 * client can be obtained (e.g. outside Forma) so the caller can show a clear
 * notice instead of throwing.
 */
async function resolveFullPullClient(
  injected: FormaFullPullClient | undefined,
): Promise<FormaFullPullClient | undefined> {
  if (injected !== undefined) {
    return injected;
  }

  try {
    return await loadAutoFormaFullPullClient();
  } catch {
    return undefined;
  }
}

/**
 * Resolves a project-snapshot client used to read the project location for
 * municipality auto-detection. An injected client wins (keeping the path
 * testable). The auto Forma SDK only connects from inside Forma's iframe, so
 * outside it (standalone preview, tests) this returns undefined and detection
 * silently no-ops.
 */
async function resolveProjectClient(
  injected: FormaProjectSnapshotClient | undefined,
): Promise<FormaProjectSnapshotClient | undefined> {
  if (injected !== undefined) {
    return injected;
  }

  if (typeof window === 'undefined' || window.self === window.top) {
    return undefined;
  }

  try {
    return await loadAutoFormaProjectSnapshotClient();
  } catch {
    return undefined;
  }
}

function createJsonDownloadUrl(value: unknown): string | null {
  if (
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function' ||
    typeof Blob === 'undefined'
  ) {
    return null;
  }

  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });

  return URL.createObjectURL(blob);
}

function apiStatusIcon(status: FormaApiCallStatus): string {
  switch (status) {
    case 'ok':
      return '✓';
    case 'error':
      return '✗';
    case 'unavailable':
      return '—';
  }
}

/** Parses an integer input, clamping to [min, max] and falling back when blank/invalid. */
function parseBoundedInt(value: string, min: number, max: number, fallback: number): number {
  const parsed = Number.parseInt(value, 10);

  if (Number.isNaN(parsed)) {
    return fallback;
  }

  return Math.min(max, Math.max(min, parsed));
}

function normalizePdfError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function formatSupabaseSaveStatus(state: SupabaseSaveState): string {
  switch (state) {
    case 'disabled':
      return 'Not configured';
    case 'idle':
      return 'Ready to save';
    case 'saving':
      return 'Saving';
    case 'saved':
      return 'Saved to Supabase';
    case 'error':
      return 'Save failed';
  }
}

function formatBackendSaveStatus(state: BackendSaveState): string {
  switch (state) {
    case 'disabled':
      return 'Not configured';
    case 'idle':
      return 'Ready to save demo record';
    case 'saving':
      return 'Saving demo record';
    case 'saved':
      return 'Saved demo record';
    case 'error':
      return 'Backend save failed';
  }
}

/**
 * Maps an analysis status to a visual badge variant so a reviewer can scan the
 * panel and spot what needs attention without reading each line. Both `error`
 * and `needs-review` collapse to the same review-required treatment, matching
 * the screening-only wording from formatAnalysisStatus.
 */
function analysisStatusVariant(status: GenericAnalysisOutput['status']): 'ok' | 'review' | 'idle' {
  switch (status) {
    case 'complete':
      return 'ok';
    case 'error':
    case 'needs-review':
      return 'review';
    case 'not-run':
      return 'idle';
  }
}

function formatAnalysisMetric(metric: NonNullable<GenericAnalysisOutput['metrics']>[number]) {
  return metric.unit === undefined ? String(metric.value) : `${metric.value} ${metric.unit}`;
}

function formatRunNote(options: {
  canGeneratePdf: boolean;
  hasReport: boolean;
  isRunning: boolean;
  pdfErrorMessage: string | null;
  pipelineError: string | null;
  pdfState: PdfState;
  pipelineState: PipelineState;
  runSummary: PipelineRunSummary;
}): string {
  if (options.isRunning) {
    return 'Collecting geometry, municipality, and building-type inputs.';
  }

  if (options.pipelineState === 'error') {
    return `Compliance pipeline failed: ${options.pipelineError ?? 'unknown error'}.`;
  }

  if (options.pdfState === 'creating') {
    return 'Preparing PDF.';
  }

  if (options.pdfState === 'ready') {
    return 'PDF ready.';
  }

  if (options.pdfState === 'error' && options.pdfErrorMessage !== null) {
    return `PDF generation failed: ${options.pdfErrorMessage}.`;
  }

  if (options.hasReport && !options.canGeneratePdf) {
    return 'PDF export needs pdfmake and bundled font VFS.';
  }

  if (options.pipelineState === 'complete' && options.runSummary !== null) {
    if (options.runSummary.needsReview === 0) {
      return `${options.runSummary.analyses} analyses prepared for report shell.`;
    }

    const reviewLabel =
      options.runSummary.needsReview === 1 ? 'analysis needs review' : 'analyses need review';

    return `${options.runSummary.analyses} analyses prepared; ${options.runSummary.needsReview} ${reviewLabel}.`;
  }

  return 'No report has been generated in this session yet.';
}
